import React, { useEffect, useState } from "react";
import { CheckCircle, FileSpreadsheet, RefreshCw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  analyzeCsvDatasetFile,
  importCsvDataset,
  type CsvDatasetImportPreview,
  type CsvDatasetImportResult,
} from "@/services/api/csvDatasetImportApi";
import * as errorUtils from "@/utils/errors";

interface CsvDatasetImportPanelProps {
  open: boolean;
  requestingOrganizationId: string | null;
  onImported: (result: CsvDatasetImportResult) => void;
}

function deriveDatasetName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, "").trim();
}

export const CsvDatasetImportPanel: React.FC<CsvDatasetImportPanelProps> = ({
  open,
  requestingOrganizationId,
  onImported,
}) => {
  const [datasetName, setDatasetName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CsvDatasetImportPreview | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setDatasetName("");
      setFile(null);
      setPreview(null);
      setIsAnalyzing(false);
      setIsImporting(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;

    if (file === null) {
      setPreview(null);
      setIsAnalyzing(false);
      return () => {
        cancelled = true;
      };
    }

    setIsAnalyzing(true);
    setError(null);

    void analyzeCsvDatasetFile(file)
      .then((nextPreview) => {
        if (!cancelled) {
          setPreview(nextPreview);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreview(null);
          setError(
            errorUtils.getErrorMessageOrFallback(
              err,
              "Failed to analyze CSV file.",
            ),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsAnalyzing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [file]);

  const handleImport = async (): Promise<void> => {
    if (file === null) {
      setError("Select a CSV file to import.");
      return;
    }

    if (requestingOrganizationId === null) {
      setError("Select an organization before importing CSV data.");
      return;
    }

    if (datasetName.trim().length === 0) {
      setError("Dataset name is required.");
      return;
    }

    setIsImporting(true);
    setError(null);

    try {
      const result = await importCsvDataset({
        file,
        datasetName,
        requestingOrganizationId,
      });
      onImported(result);
    } catch (err: unknown) {
      setError(
        errorUtils.getErrorMessageOrFallback(err, "Failed to import CSV data."),
      );
    } finally {
      setIsImporting(false);
    }
  };

  const importDisabled =
    file === null ||
    datasetName.trim().length === 0 ||
    requestingOrganizationId === null ||
    isAnalyzing ||
    isImporting ||
    preview === null;

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-foreground">
              Import CSV as a catalog source
            </h3>
            <p className="text-xs text-muted-foreground">
              Headers become schema fields. Number, boolean, and date columns
              are inferred automatically so the imported dataset can feed tables,
              charts, and other data-bound components.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-1.5 text-sm">
          <span className="font-medium text-foreground">Dataset name</span>
          <input
            type="text"
            value={datasetName}
            onChange={(event) => {
              setDatasetName(event.target.value);
              setError(null);
            }}
            placeholder="Operations telemetry"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </label>

        <label className="grid gap-1.5 text-sm">
          <span className="font-medium text-foreground">CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              const nextFile = event.target.files?.[0] ?? null;
              setFile(nextFile);
              setPreview(null);
              setError(null);
              if (nextFile !== null && datasetName.trim().length === 0) {
                setDatasetName(deriveDatasetName(nextFile.name));
              }
            }}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary"
          />
        </label>
      </div>

      {requestingOrganizationId === null && (
        <div className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-700">
          Select an organization to import CSV data into an org-scoped catalog
          source.
        </div>
      )}

      {isAnalyzing && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Analyzing CSV structure…
        </div>
      )}

      {preview !== null && (
        <div className="space-y-3 rounded-lg border border-border bg-background p-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="rounded-full bg-primary/10 px-2 py-1 text-primary">
              {preview.rowCount} rows
            </span>
            <span className="rounded-full bg-muted px-2 py-1">
              {preview.columns.length} columns
            </span>
            <span className="rounded-full bg-muted px-2 py-1">
              Delimiter {preview.delimiter === "\t" ? "tab" : preview.delimiter}
            </span>
          </div>

          <div className="grid gap-2">
            {preview.columns.map((column) => (
              <div
                key={column.fieldName}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs"
              >
                <span className="font-medium text-foreground">
                  {column.headerLabel}
                </span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                  {column.fieldName}
                </span>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                  {column.type}
                </span>
                {column.required && (
                  <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-700">
                    required
                  </span>
                )}
              </div>
            ))}
          </div>

          {preview.sampleRows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="min-w-full border-collapse text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    {preview.columns.map((column) => (
                      <th
                        key={column.fieldName}
                        className="border-b border-border px-3 py-2 text-left font-medium text-foreground"
                      >
                        {column.headerLabel}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sampleRows.map((row, rowIndex) => (
                    <tr key={`sample-row-${rowIndex}`}>
                      {row.map((cell, columnIndex) => (
                        <td
                          key={`${preview.columns[columnIndex]?.fieldName ?? columnIndex}-${rowIndex}`}
                          className="border-b border-border/60 px-3 py-2 text-muted-foreground"
                        >
                          {cell || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => {
            void handleImport();
          }}
          disabled={importDisabled}
          className="gap-2"
        >
          {isImporting ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : preview !== null ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {isImporting ? "Importing…" : "Import CSV"}
        </Button>
      </div>
    </div>
  );
};

export default CsvDatasetImportPanel;
